import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import useWaveScene from "../hooks/useWaveScene.js";
import { onIntroDone } from "../intro.js";
import "./HeroFrame.css";

// Same wave as the reference site: 50x50 grid, camera at z=1000 looking edge-on,
// flat colour, no fading, camera chases the pointer (window-relative).
const WAVE = {
  color: 0xffffff,
  fov: 75,
  cameraPos: [0, 0, 1000],
  lookAt: [0, 0, 0],
  amp: 50,
  sizeBase: 0,
  sizeMul: 300,
  distFade: [99999, 100000],
  nearFade: [-2, -1],
  mouse: [1, 1],
  ease: 0.1,
  pointer: "window",
  idle: [-590, -330], // touch screens rest where a pointer in the top-left corner of a desktop window puts the camera
};

// Scroll distance for the frame to fill the screen, as a multiple of the navbar height
const EXPAND_SCROLL = 4;

export default function HeroFrame({
  topText = ["we build what", "your business needs"],
  bottomText = ["and scale it", "as you grow"],
}) {
  const mountRef = useWaveScene(WAVE);
  const sectionRef = useRef(null);
  // Intro: the frame starts full-bleed and settles into its rounded inset, then the wave band opens
  const [frameReady, setFrameReady] = useState(false);
  const [spaceReady, setSpaceReady] = useState(false);
  // After the intro, the frame's size is scroll-driven, so its CSS transitions are switched off
  const [live, setLive] = useState(false);

  // the hero's own intro starts once the page-load logo intro hands over
  useEffect(() => {
    const timers = [];
    const off = onIntroDone(() => {
      timers.push(setTimeout(() => setFrameReady(true), 150));
      timers.push(setTimeout(() => setSpaceReady(true), 850));
      timers.push(setTimeout(() => setLive(true), 1500));
    });
    return () => {
      off();
      timers.forEach(clearTimeout);
    };
  }, []);

  // Scroll: as the navbar scrolls away (and a bit past the hero pinning), the frame
  // grows full-bleed, square and full-height. --hf-p goes 0 -> 1 over that distance.
  // Layout is measured only on resize; per frame we just read scrollY and write one
  // variable (when it changes), on GSAP's ticker so it runs in step with Lenis.
  useEffect(() => {
    const el = sectionRef.current;
    let navHeight = 0;
    let range = 1;
    let lastP = -1;

    const measure = () => {
      // Measure the navbar via the (non-sticky) parent: once the hero is pinned,
      // its own offsetTop includes the sticky offset and grows with the scroll.
      navHeight = el.parentElement.getBoundingClientRect().top + window.scrollY;
      range = Math.max(navHeight, 100) * EXPAND_SCROLL;
      el.style.setProperty("--hf-nav", `${navHeight}px`);
      // Scroll beyond the pin point: reserve it below the hero so the next
      // section only starts rising once the frame fills the screen.
      el.style.setProperty("--hf-extra", `${Math.max(0, range - navHeight)}px`);
      lastP = -1;
      update();
    };

    const update = () => {
      const p = Math.round(Math.min(1, Math.max(0, window.scrollY / range)) * 10000) / 10000;
      if (p === lastP) return;
      lastP = p;
      el.style.setProperty("--hf-p", p);
    };

    measure();
    gsap.ticker.add(update);
    window.addEventListener("resize", measure);
    // the navbar's height settles once its logo/fonts load
    const header = document.querySelector(".navbar3");
    const ro = header ? new ResizeObserver(measure) : null;
    ro?.observe(header);
    return () => {
      gsap.ticker.remove(update);
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, []);

  return (
    <section
      className={`hf${live ? " is-live" : ""}${frameReady ? " is-in" : ""}`}
      ref={sectionRef}
      data-nav-theme="dark"
    >
      <div className={`hf-frame${frameReady ? "" : " is-normal"}`}>
        <h1 className="hf-heading">
          {topText.map((line, i) => (
            <span key={line} className="hf-line">
              <span style={{ transitionDelay: `${0.25 + i * 0.09}s` }}>{line}</span>
            </span>
          ))}
          {/* the sentence finishes below the canvas; keep the whole of it in the h1 for crawlers and screen readers */}
          <span className="hf-sr-only"> {bottomText.join(" ")}</span>
        </h1>

        <div className={`hf-space${spaceReady ? "" : " is-shrink"}`}>
          <div className="hf-canvas" ref={mountRef} aria-hidden="true" />
        </div>

        <p className="hf-heading is-right" aria-hidden="true">
          {bottomText.map((line, i) => (
            <span key={line} className="hf-line">
              <span style={{ transitionDelay: `${0.8 + i * 0.09}s` }}>{line}</span>
            </span>
          ))}
        </p>
      </div>
    </section>
  );
}
